/**
 * Өртгийн тооцоо.
 *
 * ЗАРЧИМ
 *  - Мөнгө = Decimal (decimal.js). Float ХЭЗЭЭ Ч биш.
 *  - Ханш худалдан авах мөчид ТҮГЖИГДЭНЭ (items.purchaseRate).
 *  - landedCost = goodsCost + хуваарилсан нэмэлт зардал.
 *  - Хуваарилалт НЭГ Л УДАА (purchases.costedAt).
 */
import { Decimal } from 'decimal.js';
import { canSeeCost } from './permissions.js';
// Мөнгөний тооцоонд 2 орон, ханшид 6 орон
Decimal.set({ precision: 28, rounding: Decimal.ROUND_HALF_UP });
export const money = (v) => new Decimal(v);
export const ZERO = new Decimal(0);
/**
 * Өртгийг илтгэх талбарын нэр — хариуны аль ч гүнд байсан `null` болно.
 *
 * ⚠️ Шинэ өртгийн талбар API-д нэмбэл ЭНД нэмнэ. Нэрээр нь барьдаг тул
 *    route бүр өөрийн жагсаалт барихгүй — нэг нь мартагдаж задрахгүй.
 */
export const COST_FIELDS = new Set([
    'landedCostMnt',
    'goodsCostMnt',
    'overheadMnt',
    'purchaseAmount',
    'purchaseRate',
    'purchaseRateDate',
    'lossMnt',
    'missingCostMnt',
    'costMntAtSale',
]);
const isPlainObject = (v) => typeof v === 'object' && v !== null && Object.getPrototypeOf(v) === Object.prototype;
/**
 * Хариунаас өртгийн талбарыг хасна (утгыг `null` болгоно, түлхүүр үлдэнэ).
 *
 * Зөвхөн энгийн объект, массив руу орно — `Decimal`, `Date` нь навч.
 * Эх объектыг ӨӨРЧЛӨХГҮЙ, шинэ хуулбар буцаана.
 *
 * ⚠️ Төрөл нь `T` хэвээр — `null` болсон талбар төрөлд string хэвээр
 *    харагдана. Клиент өртгийн талбарыг `COST_ROLES`-оор л уншина.
 */
export function stripCost(value) {
    if (Array.isArray(value))
        return value.map((v) => stripCost(v));
    if (!isPlainObject(value))
        return value;
    const out = {};
    for (const [k, v] of Object.entries(value)) {
        out[k] = COST_FIELDS.has(k) ? null : stripCost(v);
    }
    return out;
}
/** Ролийн эрхээр — өртөг харах эрхгүй бол `stripCost`. */
export function maskCost(role, value) {
    return canSeeCost(role) ? value : stripCost(value);
}
/** Төгрөг рүү хөрвүүлэх — 2 орон хүртэл. */
export function toMnt(amount, rate) {
    return new Decimal(amount).times(rate).toDecimalPlaces(2);
}
/**
 * Нэмэлт зардлыг нэгжүүдэд хуваарилна.
 *
 * BY_VALUE — барааны үнийн хувь тэнцүүлэн (үнэтэй гутал их зардал үүрнэ)
 * BY_UNIT  — нэгж бүрт тэнцүү
 *
 * Дугуйруулалтын зөрүүг СҮҮЛИЙН нэгжид нэмнэ. Ингэснээр
 * sum(overheads) === overheadTotal яг таарна — нягтлан бодохад чухал.
 */
export function allocateOverhead(input) {
    const { method } = input;
    const goods = input.goodsCosts.map((g) => (g === null ? null : new Decimal(g)));
    const total = new Decimal(input.overheadTotal);
    if (goods.length === 0) {
        throw new Error('allocateOverhead: нэгж байхгүй');
    }
    if (method === 'BY_VALUE' && hasUncostedGoods(input.goodsCosts)) {
        throw new Error('allocateOverhead: ₮ өртөггүй нэгжтэй үед BY_VALUE боломжгүй');
    }
    if (total.isNegative()) {
        throw new Error('allocateOverhead: нэмэлт зардал сөрөг байж болохгүй');
    }
    if (goods.some((g) => g?.isNegative())) {
        throw new Error('allocateOverhead: барааны өртөг сөрөг байж болохгүй');
    }
    const n = goods.length;
    const overheads = new Array(n);
    if (total.isZero()) {
        overheads.fill(ZERO);
        return {
            overheads,
            landedCosts: goods.map((g) => g?.toDecimalPlaces(2) ?? null),
            roundingAdjustment: ZERO,
        };
    }
    if (method === 'BY_UNIT') {
        const per = total.dividedBy(n).toDecimalPlaces(2, Decimal.ROUND_DOWN);
        for (let i = 0; i < n; i++)
            overheads[i] = per;
    }
    else {
        // BY_VALUE-д бүх нэгж ₮ өртөгтэй нь дээр шалгагдсан
        const valued = goods;
        const goodsSum = valued.reduce((a, b) => a.plus(b), ZERO);
        if (goodsSum.isZero()) {
            // Бүх өртөг 0 → BY_UNIT руу шилжинэ
            return allocateOverhead({ ...input, method: 'BY_UNIT' });
        }
        for (let i = 0; i < n; i++) {
            overheads[i] = total
                .times(valued[i])
                .dividedBy(goodsSum)
                .toDecimalPlaces(2, Decimal.ROUND_DOWN);
        }
    }
    // Дугуйруулалтын үлдэгдлийг сүүлийн нэгжид
    const allocated = overheads.reduce((a, b) => a.plus(b), ZERO);
    const diff = total.minus(allocated);
    if (!diff.isZero()) {
        overheads[n - 1] = overheads[n - 1].plus(diff);
    }
    const landedCosts = goods.map((g, i) => g?.plus(overheads[i]).toDecimalPlaces(2) ?? null);
    return { overheads, landedCosts, roundingAdjustment: diff };
}
/** ₮ өртөг тооцоогүй нэгж байгаа эсэх — тийм бол BY_VALUE хуваарилалт боломжгүй. */
export function hasUncostedGoods(goodsCosts) {
    return goodsCosts.some((g) => g === null);
}
export function calcMargin(salePrice, landedCost) {
    const price = new Decimal(salePrice);
    const cost = new Decimal(landedCost);
    const profit = price.minus(cost).toDecimalPlaces(2);
    const marginPct = price.isZero()
        ? ZERO
        : profit.dividedBy(price).times(100).toDecimalPlaces(2);
    return { profit, marginPct };
}
/** Урамшуулал: цэвэр борлуулалтаас. Буцаагдсан мөр тооцогдохгүй. */
export function calcCommission(lineTotal, percent) {
    const pct = new Decimal(percent);
    if (pct.isNegative() || pct.greaterThan(100)) {
        throw new Error(`calcCommission: хувь 0..100 хооронд байх ёстой, ирсэн: ${pct}`);
    }
    return new Decimal(lineTotal).times(pct).dividedBy(100).toDecimalPlaces(2);
}
//# sourceMappingURL=cost.js.map